import React, { useState, useEffect } from "react";
import { Box } from "./seroPreviewStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";


const MAX_TITLE_PIXELS = 600;
const MAX_DESC_PIXELS = 960;

const measurePixelWidth = (text: string, font = "14px Arial"): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (context) {
    context.font = font;
    return context.measureText(text).width;
  }
  return 0;
};

const getColorByPixels = (px: number) => {
  if (px < 150) return "red";
  if (px <= 600) return "green";
  return "red";
};
const getDescColor = (px: number) => {
  if (px < 600) return "red";    
  if (px <= 960) return "green";  
  return "red";                 
};


const SerpPreviewSimulator: React.FC = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
  const height = document.body.scrollHeight;
  window.parent.postMessage({ type: "setHeight", height }, "*");
}, [title, description, url, keywords]);


  const boldKeywords = (text: string, keywords: string): string => {
    let result = text;
    keywords.split(/[\s,]+/).forEach((word) => {
      if (word.trim()) {
        const regex = new RegExp(`(${word.trim()})`, "gi");
        result = result.replace(regex, "<strong>$1</strong>");
      }
    });
    return result;
  };

  const handleFetchData = async () => {
    if (!url) {
      alert("Please enter a valid URL");
      return;
    }

    try {
      new URL(url);
    } catch {
      alert("Invalid URL format");
      return;
    }

    setLoading(true);

    const corsProxies = [
      "https://corsproxy.io/?",
      "https://api.codetabs.com/v1/proxy?quest=",
    ];

    for (const proxy of corsProxies) {
      try {
        const res = await fetch(proxy + url);
        if (!res.ok) throw new Error("Error");
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const metaTitle = doc.querySelector("title")?.textContent || "";
        const metaDesc =
          doc.querySelector('meta[name="description"]')?.getAttribute("content") ||
          doc.querySelector('meta[property="og:description"]')?.getAttribute("content") ||
          "";

        setTitle(metaTitle.trim());
        setDescription(metaDesc.trim());
        setLoading(false);
        return;
      } catch { }
    }

    alert("Error fetching metadata.");
    setLoading(false);
  };

  const capitalizeTitle = () =>
    setTitle(title.replace(/\b\w/g, (c) => c.toUpperCase()));

  const titlePixel = measurePixelWidth(title, "20px Arial");
  const descPixel = measurePixelWidth(description, "14px Arial");

  const titleBarWidth = Math.min((titlePixel / MAX_TITLE_PIXELS) * 100, 100);
  const descBarWidth = Math.min((descPixel / MAX_DESC_PIXELS) * 100, 100);

  const titleColor = getColorByPixels(titlePixel);
  const descColor = getDescColor(descPixel);

  const getPreviewTitle = () => {
    if (!title) return "";

    if (titlePixel <= MAX_TITLE_PIXELS) return title;

    const words = title.split(" ");
    let trimmed = "";

    for (let i = 0; i < words.length; i++) {
      const test = (trimmed + " " + words[i]).trim();

      if (measurePixelWidth(test + "...", "20px Arial") > MAX_TITLE_PIXELS) {
        break; 
      }

      trimmed = test;
    }

    return trimmed + "...";
  };

  const getPreviewDescription = () => {
    if (!description) return "";

    if (descPixel <= MAX_DESC_PIXELS) return description;

    const words = description.split(" ");
    let trimmed = "";

    for (let i = 0; i < words.length; i++) {
      const test = (trimmed + " " + words[i]).trim();

      if (measurePixelWidth(test + "...", "14px Arial") > MAX_DESC_PIXELS) {
        break; 
      }

      trimmed = test;
    }

    return trimmed + "...";
  };



  const previewTitle = getPreviewTitle();


  const previewDescription = boldKeywords(
    description ||
    "Here is an example of what a snippet looks like in Google's SERPs.",
    keywords
  );

  return (
    <Box>
      <div
        className={`container container_sero ${!isInIframe ? "defaultText" : "interDisplay"
          }`}
      >
        {!isInIframe && (
          <ToolsHeader
            title="SERP Preview Simulator"
            description="Simulate exactly how your webpage looks on Google search results."
          />
        )}

        <div className="grid">
          <div className="card">
            <div className="button-group">
              <button
                onClick={handleFetchData}
                className="button button-primary"
                disabled={loading}
                style={{ backgroundColor: loading ? "#ccc" : "#8cc540" }}
              >
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {loading ? "Fetching..." : "Fetch Data"}
              </button>

              <button
                onClick={capitalizeTitle}
                className="button button-primary"
                style={{ backgroundColor: "#8cc540" }}
              >
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                Capitalize Title
              </button>
            </div>


            <div className="form-group">
              <label>URL</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>

            {/* TITLE */}
            <div className="form-group">
              <label>
                Title Tag{" "}
                <span>
                  {title.length} chars ({Math.round(titlePixel)}/
                  {MAX_TITLE_PIXELS}px)
                </span>
              </label>

              <div className="pixel-bar-wrapper">
                <div
                  className="pixel-bar"
                  style={{
                    width: `${titleBarWidth}%`,
                    backgroundColor: titleColor,
                  }}
                ></div>
              </div>

              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter your title"
                className={`meta-input ${titlePixel > MAX_TITLE_PIXELS ? "error" : ""
                  }`}
              />

              {titlePixel > MAX_TITLE_PIXELS && (
                <div className="warning-box">
                  The title is wider than 600px and may not be displayed in full
                  length.
                </div>
              )}
            </div>

            <div className="form-group">
              <label>
                Meta Description{" "}
                <span>
                  {description.length} chars ({Math.round(descPixel)}/
                  {MAX_DESC_PIXELS}px)
                </span>
              </label>

              <div className="pixel-bar-wrapper">
                <div
                  className="pixel-bar"
                  style={{
                    width: `${descBarWidth}%`,
                    backgroundColor: descColor,
                  }}
                ></div>
              </div>

              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter your meta description"
                className={`meta-textarea ${descPixel > MAX_DESC_PIXELS ? "error" : ""
                  }`}
              ></textarea>

              {descPixel > MAX_DESC_PIXELS && (
                <div className="warning-box">
                  Description is wider than 960px and may get trimmed by Google.
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Keywords to Bold (separate by spaces or commas)</label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="keyword1, keyword2"
              />
            </div>
          </div>

          <div className="serp-preview1">
            <div className="search-header uk-flex">
              <div className="search-logo">
                <img
                  width="92"
                  alt="Google Logo"
                  src="https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_272x92dp.png"
                />
              </div>

              <div className="formgoogle">
                <form className="serp-form-tag">
                  <strong className="mg-label font-10 font-montserrat uk-position-top-left mg-margin-t-15 mg-margin-l-15">
                    {"TIP :"}
                  </strong>
                  <input
                    className="search-input"
                    placeholder="Enter a keyword to get real search results"
                  />
                </form>

                <div>
                  <div className="search-menu uk-flex">
                    <div className="search-cat is-active">All</div>
                    <div className="search-cat">Images</div>
                    <div className="search-cat">Videos</div>
                    <div className="search-cat">News</div>
                    <div className="search-cat">Maps</div>
                    <div className="search-cat">More</div>
                  </div>

                  <div className="search-count">
                    About 600,000,000 results (0.43 seconds)
                  </div>

                  <div className="serp-url">{url || "www.example.com"}</div>
                  <div className="serp-title">
                    {title ? previewTitle : "Sample Title"}
                  </div>


                  <div
                    className="serp-description"
                    dangerouslySetInnerHTML={{
                      __html: boldKeywords(
                        getPreviewDescription(),
                        keywords
                      ),
                    }}
                  ></div>


                  {(url ? ["1", "2"] : ["1"]).map((id) => (
                    <div key={id} className="serp-result is-placeholder">
                      <span className="snippet-url"></span>
                      <span className="snippet-title"></span>
                      <span className="snippet-descr"></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Box>
  );
};

export default SerpPreviewSimulator;